import { useLocation } from 'react-router-dom';
import { TAddPasswordPost } from '../api/types';
import API from '../api/api';
import GlobalContext from './GlobalContext';
import { createContext, FC, useContext, useEffect, useState } from 'react';
import { EMixpanelEvent, EQueryStringParam } from '../util/constants';
import mixpanel from 'mixpanel-browser';

type TFacebookAddPasswordContext = {
    facebookAddPasswordUiState: EFacebookAddPasswordUiState;
    setFacebookAddPasswordUiState: (state: EFacebookAddPasswordUiState) => void;
    email: string;
    savePassword: (password: string) => Promise<void>;
};

export enum EFacebookAddPasswordUiState {
    default,
    queryStringParsed,
    ready,
    fatalError,
    success,
}

const FacebookAddPasswordContext = createContext<TFacebookAddPasswordContext | undefined>(undefined);

export const FacebookAddPasswordProvider: FC = ({ children }) => {
    const { setSubscriberId, getFullLoginLinkFromLocalStorage, getCoBranding } = useContext(GlobalContext);
    const [facebookAddPasswordUiState, setFacebookAddPasswordUiState] = useState(EFacebookAddPasswordUiState.default);
    const [userId, setUserId] = useState('');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const { search: queryParams } = useLocation();

    const parseQueryString = () => {
        const params = new URLSearchParams(queryParams);
        try {
            const userId = params.get(EQueryStringParam.userId);
            const parsedEmail = atob(params.get(EQueryStringParam.email));
            const code = params.get(EQueryStringParam.code);
            const subscriberId = params.get(EQueryStringParam.subscriberId);
            if (userId && parsedEmail && code) {
                setUserId(userId);
                setEmail(parsedEmail);
                setCode(code);
                subscriberId && setSubscriberId(subscriberId);
                setFacebookAddPasswordUiState(EFacebookAddPasswordUiState.queryStringParsed);
            } else {
                throw Error('Missing or invalid query params');
            }
        } catch {
            setFacebookAddPasswordUiState(EFacebookAddPasswordUiState.fatalError);
            console.error('Failed to parse query string', { cause: `Invalid query string ---> ${params.toString()}` });
        }
    };

    const getInitialData = async () => {
        await getCoBranding();
        setFacebookAddPasswordUiState(EFacebookAddPasswordUiState.ready);
    };

    const savePassword = async (password: string) => {
        const payload: TAddPasswordPost = {
            code,
            password,
        };
        try {
            await API.post.addPassword(payload, userId);
            mixpanel.track(EMixpanelEvent.actionCompleted, { key: 'deprecate_facebook' });
            setFacebookAddPasswordUiState(EFacebookAddPasswordUiState.success);
        } catch (error) {
            mixpanel.track(EMixpanelEvent.actionFailed, { key: 'deprecate_facebook' });
            throw Error('Failed to save password', { cause: error.response?.status });
        }
    };

    useEffect(() => {
        mixpanel.track(EMixpanelEvent.viewed, { key: 'deprecate_facebook_password' });
    }, []);

    useEffect(() => {
        switch (facebookAddPasswordUiState) {
            case EFacebookAddPasswordUiState.default:
                parseQueryString();
                break;
            case EFacebookAddPasswordUiState.queryStringParsed:
                getInitialData();
                break;
            case EFacebookAddPasswordUiState.ready:
                // local storage set during Facebook login
                getFullLoginLinkFromLocalStorage();
                mixpanel.identify(userId);
                break;
            default:
                break;
        }
    });

    return (
        <FacebookAddPasswordContext.Provider
            value={{
                facebookAddPasswordUiState,
                setFacebookAddPasswordUiState,
                savePassword,
                email,
            }}
        >
            {children}
        </FacebookAddPasswordContext.Provider>
    );
};

export default FacebookAddPasswordContext;
